import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  Checkbox,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
  LinearProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import PreviewOutlinedIcon from '@mui/icons-material/PreviewOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import proxy from '../../api/axios';

const ACCENT = '#137274';
const PINK = '#f50057';
const DRAFT_KEY = 'ch_admin_broadcast_draft_v1';

const SAMPLE_HTML = `<h1 style="margin:0 0 12px;font-size:22px;font-weight:800;color:#0f2a2b;letter-spacing:-0.03em;">
  Hi {{userName}}, a quick update from CrafterHack
</h1>
<p style="margin:0 0 14px;font-size:15px;line-height:1.65;color:#5f6b73;">
  Paste your HTML here — headings, lists, links, and inline styles all work.
</p>
<p style="margin:0;font-size:14px;line-height:1.6;color:#5f6b73;">
  <a href="https://crafterhack.com" style="color:#137274;font-weight:700;">Open CrafterHack →</a>
</p>`;

const DEFAULT_DRAFT = {
  audience: 'all',
  selectedIds: [],
  subject: '',
  preheader: '',
  eyebrow: 'Announcement',
  format: 'html',
  body: SAMPLE_HTML,
  wrapInLayout: true,
};

const readDraft = () => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      ...DEFAULT_DRAFT,
      ...parsed,
      selectedIds: Array.isArray(parsed.selectedIds) ? parsed.selectedIds.map(String) : [],
      audience: parsed.audience === 'selected' ? 'selected' : 'all',
      format: parsed.format === 'text' ? 'text' : 'html',
      wrapInLayout: parsed.wrapInLayout !== false,
      subject: typeof parsed.subject === 'string' ? parsed.subject : '',
      preheader: typeof parsed.preheader === 'string' ? parsed.preheader : '',
      eyebrow:
        typeof parsed.eyebrow === 'string' && parsed.eyebrow.trim()
          ? parsed.eyebrow
          : 'Announcement',
      body: typeof parsed.body === 'string' ? parsed.body : SAMPLE_HTML,
      savedAt: parsed.savedAt || null,
    };
  } catch {
    return null;
  }
};

const writeDraft = (draft) => {
  try {
    localStorage.setItem(
      DRAFT_KEY,
      JSON.stringify({
        ...draft,
        savedAt: new Date().toISOString(),
      }),
    );
    return true;
  } catch {
    return false;
  }
};

const clearDraftStorage = () => {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* ignore */
  }
};

const AdminBroadcastEmail = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.12)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const pageBg = isDark
    ? '#0f1419'
    : 'linear-gradient(180deg, #eef6f8 0%, #f7f9fb 40%, #ffffff 100%)';

  const initialDraft = useMemo(() => readDraft(), []);
  const hadMeaningfulDraft = Boolean(
    initialDraft &&
      (String(initialDraft.subject || '').trim() ||
        String(initialDraft.preheader || '').trim() ||
        (initialDraft.body && initialDraft.body !== SAMPLE_HTML) ||
        (initialDraft.audience === 'selected' &&
          Array.isArray(initialDraft.selectedIds) &&
          initialDraft.selectedIds.length > 0) ||
        initialDraft.format === 'text' ||
        initialDraft.wrapInLayout === false ||
        (initialDraft.eyebrow && initialDraft.eyebrow !== 'Announcement')),
  );

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [audience, setAudience] = useState(initialDraft?.audience || 'all');
  const [selected, setSelected] = useState(
    () => new Set(initialDraft?.selectedIds || []),
  );
  const [userQuery, setUserQuery] = useState('');
  const [subject, setSubject] = useState(initialDraft?.subject || '');
  const [preheader, setPreheader] = useState(initialDraft?.preheader || '');
  const [eyebrow, setEyebrow] = useState(initialDraft?.eyebrow || 'Announcement');
  const [format, setFormat] = useState(initialDraft?.format || 'html');
  const [body, setBody] = useState(initialDraft?.body || SAMPLE_HTML);
  const [wrapInLayout, setWrapInLayout] = useState(
    initialDraft ? initialDraft.wrapInLayout !== false : true,
  );
  const [previewTab, setPreviewTab] = useState('compose');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [jobStatus, setJobStatus] = useState(null); // queued | running | completed | failed
  const [activeJobId, setActiveJobId] = useState(null);
  const [draftSavedAt, setDraftSavedAt] = useState(initialDraft?.savedAt || null);
  const [draftHydrated] = useState(hadMeaningfulDraft);
  const pollRef = useRef(null);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const { data } = await proxy.get('/users/admin/email-directory');
      setUsers(data.users || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not load users');
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (draftHydrated) {
      toast.info('Restored broadcast draft from this browser', { autoClose: 2500 });
    }
    // once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save draft (debounced)
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const ok = writeDraft({
        audience,
        selectedIds: Array.from(selected),
        subject,
        preheader,
        eyebrow,
        format,
        body,
        wrapInLayout,
      });
      if (ok) setDraftSavedAt(new Date().toISOString());
    }, 400);
    return () => window.clearTimeout(timer);
  }, [
    audience,
    selected,
    subject,
    preheader,
    eyebrow,
    format,
    body,
    wrapInLayout,
  ]);

  const clearDraft = () => {
    clearDraftStorage();
    setAudience('all');
    setSelected(new Set());
    setSubject('');
    setPreheader('');
    setEyebrow('Announcement');
    setFormat('html');
    setBody(SAMPLE_HTML);
    setWrapInLayout(true);
    setDraftSavedAt(null);
    setResult(null);
    toast.success('Draft cleared');
  };

  const filteredUsers = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.username?.toLowerCase().includes(q),
    );
  }, [users, userQuery]);

  const recipientCount =
    audience === 'all' ? users.length : selected.size;

  const draftLabel = useMemo(() => {
    if (!draftSavedAt) return null;
    try {
      const d = new Date(draftSavedAt);
      if (Number.isNaN(d.getTime())) return 'Draft saved';
      return `Draft saved ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } catch {
      return 'Draft saved';
    }
  }, [draftSavedAt]);

  const toggleUser = (id) => {
    const key = String(id);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      filteredUsers.forEach((u) => next.add(String(u.id)));
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const previewHtml = useMemo(() => {
    const name = 'Alex';
    const baseUrl = 'https://crafterhack.com';
    let content = (body || '')
      .replace(/\{\{\s*userName\s*\}\}/gi, name)
      .replace(/\{\{\s*name\s*\}\}/gi, name)
      .replace(/\{\{\s*email\s*\}\}/gi, 'alex@example.com')
      .replace(/\{\{\s*appUrl\s*\}\}/gi, baseUrl);

    if (format === 'text') {
      content = content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br/>');
      content = `<div style="font-size:15px;line-height:1.65;color:#5f6b73;">${content}</div>`;
    }

    if (!wrapInLayout) {
      return content;
    }

    return `
      <div style="background:#f4f8f9;padding:20px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
        <div style="max-width:560px;margin:0 auto;background:#fff;border:1px solid #e5ecee;border-radius:12px;overflow:hidden;">
          <div style="padding:18px 24px;border-bottom:1px solid #e5ecee;background:linear-gradient(180deg,#f0f7f7,#fff);">
            <div style="display:flex;align-items:center;gap:10px;">
              <div style="width:36px;height:36px;border-radius:8px;background:#0a0a0a;color:#fff;font-weight:900;display:flex;align-items:center;justify-content:center;font-size:14px;">
                <span>C</span><span style="color:#f50057;">H</span>
              </div>
              <div>
                <div style="font-weight:800;color:#0f2a2b;font-size:14px;">CrafterHack</div>
                <div style="font-size:11px;font-weight:700;color:#137274;letter-spacing:0.06em;text-transform:uppercase;">${eyebrow || 'Announcement'}</div>
              </div>
            </div>
          </div>
          <div style="padding:24px;">${content}</div>
          <div style="padding:14px 24px;border-top:1px solid #e5ecee;font-size:12px;color:#8a969c;">
            You’re receiving this because you have a CrafterHack account.
          </div>
        </div>
      </div>`;
  }, [body, format, wrapInLayout, eyebrow]);

  const openConfirm = () => {
    if (!subject.trim()) {
      toast.error('Add a subject line');
      return;
    }
    if (!body.trim()) {
      toast.error('Add an email body');
      return;
    }
    if (audience === 'selected' && selected.size === 0) {
      toast.error('Select at least one user');
      return;
    }
    if (recipientCount === 0) {
      toast.error('No recipients');
      return;
    }
    setConfirmOpen(true);
  };

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const pollJob = useCallback(
    (jobId) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await proxy.get(`/users/admin/broadcast-email/${jobId}`);
          const job = data.job;
          if (!job) return;
          setJobStatus(job.status);
          if (job.status === 'completed') {
            stopPolling();
            setActiveJobId(null);
            setResult(job.results || null);
            toast.success(
              `Broadcast finished — sent ${job.results?.sent ?? 0} of ${job.results?.total ?? job.total}`,
            );
          } else if (job.status === 'failed') {
            stopPolling();
            setActiveJobId(null);
            toast.error(job.error || 'Broadcast failed');
          }
        } catch (_) {
          // Keep polling; transient network blips shouldn't kill the job view
        }
      }, 2500);
    },
    [stopPolling],
  );

  // Resume status if a job was already running when the page opened
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await proxy.get('/users/admin/broadcast-email-active');
        if (cancelled || !data.job) return;
        setActiveJobId(data.job.id);
        setJobStatus(data.job.status);
        pollJob(data.job.id);
      } catch (_) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pollJob]);

  const send = async () => {
    setSending(true);
    setResult(null);
    setJobStatus(null);
    try {
      const { data } = await proxy.post(
        '/users/admin/broadcast-email',
        {
          subject: subject.trim(),
          body,
          format,
          wrapInLayout,
          audience,
          userIds: audience === 'selected' ? Array.from(selected) : [],
          preheader: preheader.trim(),
          eyebrow: eyebrow.trim() || 'Announcement',
        },
        // Only waits for queue accept + SMTP verify — send runs in background
        { timeout: 45 * 1000 },
      );
      setConfirmOpen(false);
      setActiveJobId(data.jobId);
      setJobStatus(data.status || 'queued');
      toast.success(data.message || 'Broadcast queued');
      if (data.jobId) pollJob(data.jobId);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Broadcast failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: pageBg, px: { xs: 1.5, sm: 2.5 }, py: 2.5 }}>
      <Helmet>
        <title>Send Email | Admin</title>
      </Helmet>

      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        {/* Header */}
        <Box
          sx={{
            mb: 3,
            p: { xs: 2.25, sm: 3 },
            borderRadius: 3,
            border: `1px solid ${border}`,
            bgcolor: surface,
            background: isDark
              ? `linear-gradient(120deg, ${alpha(ACCENT, 0.22)}, transparent 55%)`
              : `linear-gradient(120deg, ${alpha(ACCENT, 0.1)}, transparent 55%)`,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(ACCENT, isDark ? 0.28 : 0.14),
                color: ACCENT,
              }}
            >
              <MailOutlineIcon />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: '1.45rem', letterSpacing: '-0.03em' }}>
                Broadcast email
              </Typography>
              <Typography variant="body2" sx={{ color: muted, maxWidth: 520 }}>
                Send plain text or HTML to everyone — or hand-pick recipients. Use{' '}
                <code style={{ fontWeight: 700 }}>{'{{userName}}'}</code> to personalize.
                Drafts auto-save in this browser.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            {draftLabel && (
              <Chip
                icon={<SaveOutlinedIcon sx={{ fontSize: '16px !important' }} />}
                label={draftLabel}
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: alpha(ACCENT, isDark ? 0.2 : 0.1),
                  color: ACCENT,
                  '& .MuiChip-icon': { color: ACCENT },
                }}
              />
            )}
            <Chip
              icon={<GroupsOutlinedIcon sx={{ fontSize: '16px !important' }} />}
              label={`${recipientCount} recipient${recipientCount === 1 ? '' : 's'}`}
              sx={{
                fontWeight: 800,
                bgcolor: alpha(ACCENT, isDark ? 0.25 : 0.12),
                color: ACCENT,
                '& .MuiChip-icon': { color: ACCENT },
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '340px minmax(0, 1fr)' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          {/* Audience panel */}
          <Box
            sx={{
              borderRadius: 3,
              border: `1px solid ${border}`,
              bgcolor: surface,
              overflow: 'hidden',
              position: { lg: 'sticky' },
              top: { lg: 16 },
            }}
          >
            <Box sx={{ px: 2, py: 1.75, borderBottom: `1px solid ${border}` }}>
              <Typography sx={{ fontWeight: 800, mb: 1.25 }}>Audience</Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={audience}
                onChange={(_e, v) => v && setAudience(v)}
                sx={{
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    fontWeight: 700,
                    py: 0.85,
                  },
                  '& .Mui-selected': {
                    bgcolor: `${alpha(ACCENT, 0.15)} !important`,
                    color: `${ACCENT} !important`,
                  },
                }}
              >
                <ToggleButton value="all">
                  <GroupsOutlinedIcon sx={{ fontSize: 16, mr: 0.75 }} />
                  All users
                </ToggleButton>
                <ToggleButton value="selected">
                  <PersonSearchOutlinedIcon sx={{ fontSize: 16, mr: 0.75 }} />
                  Selected
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {audience === 'all' ? (
              <Box sx={{ p: 2.25 }}>
                <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                  {loadingUsers ? '…' : users.length} users
                </Typography>
                <Typography variant="body2" sx={{ color: muted, fontSize: '0.85rem', lineHeight: 1.5 }}>
                  Everyone with an email on file will receive this message. Sends are throttled
                  automatically.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: 520 }}>
                <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search name or email…"
                    value={userQuery}
                    onChange={(e) => setUserQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18, color: muted }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                    <Button size="small" onClick={selectFiltered} sx={{ textTransform: 'none', fontWeight: 700 }}>
                      Select filtered
                    </Button>
                    <Button size="small" onClick={clearSelection} sx={{ textTransform: 'none', fontWeight: 700, color: muted }}>
                      Clear
                    </Button>
                    <Tooltip title="Refresh directory">
                      <IconButton size="small" onClick={loadUsers}>
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pb: 1.5 }}>
                  {loadingUsers ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={28} sx={{ color: ACCENT }} />
                    </Box>
                  ) : filteredUsers.length === 0 ? (
                    <Typography variant="body2" sx={{ color: muted, textAlign: 'center', py: 3 }}>
                      No matches
                    </Typography>
                  ) : (
                    filteredUsers.map((u) => {
                      const id = String(u.id);
                      const checked = selected.has(id);
                      return (
                        <Box
                          key={id}
                          onClick={() => toggleUser(id)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 0.75,
                            py: 0.65,
                            borderRadius: 1.5,
                            cursor: 'pointer',
                            bgcolor: checked ? alpha(ACCENT, isDark ? 0.18 : 0.08) : 'transparent',
                            '&:hover': { bgcolor: alpha(ACCENT, isDark ? 0.12 : 0.05) },
                          }}
                        >
                          <Checkbox size="small" checked={checked} tabIndex={-1} sx={{ p: 0.5 }} />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                              {u.name}
                            </Typography>
                            <Typography noWrap variant="caption" sx={{ color: muted }}>
                              {u.email}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Box>
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderTop: `1px solid ${border}`,
                    bgcolor: isDark ? alpha('#fff', 0.02) : alpha(ACCENT, 0.03),
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 800, color: ACCENT }}>
                    {selected.size} selected
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Composer */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                p: 2.25,
                borderRadius: 3,
                border: `1px solid ${border}`,
                bgcolor: surface,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={format}
                  onChange={(_e, v) => {
                    if (!v) return;
                    setFormat(v);
                    if (v === 'text' && body === SAMPLE_HTML) {
                      setBody('Hi {{userName}},\n\nWrite your message here.\n\n— CrafterHack');
                    }
                    if (v === 'html' && body.startsWith('Hi {{userName}}')) {
                      setBody(SAMPLE_HTML);
                    }
                  }}
                  sx={{
                    '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700 },
                    '& .Mui-selected': {
                      bgcolor: `${alpha(ACCENT, 0.15)} !important`,
                      color: `${ACCENT} !important`,
                    },
                  }}
                >
                  <ToggleButton value="html">
                    <CodeOutlinedIcon sx={{ fontSize: 16, mr: 0.75 }} />
                    HTML / markup
                  </ToggleButton>
                  <ToggleButton value="text">
                    <NotesOutlinedIcon sx={{ fontSize: 16, mr: 0.75 }} />
                    Plain text
                  </ToggleButton>
                </ToggleButtonGroup>

                <FormControlLabel
                  control={
                    <Switch
                      checked={wrapInLayout}
                      onChange={(e) => setWrapInLayout(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                      Wrap in CrafterHack layout
                    </Typography>
                  }
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <TextField
                  label="Subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  fullWidth
                  required
                  placeholder="What’s new on CrafterHack"
                />
                <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <TextField
                    label="Eyebrow (layout)"
                    value={eyebrow}
                    onChange={(e) => setEyebrow(e.target.value)}
                    fullWidth
                    size="small"
                    disabled={!wrapInLayout}
                  />
                  <TextField
                    label="Preheader (inbox preview)"
                    value={preheader}
                    onChange={(e) => setPreheader(e.target.value)}
                    fullWidth
                    size="small"
                    disabled={!wrapInLayout}
                  />
                </Box>

                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={previewTab}
                  onChange={(_e, v) => v && setPreviewTab(v)}
                  sx={{
                    alignSelf: 'flex-start',
                    '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700 },
                    '& .Mui-selected': {
                      bgcolor: `${alpha(PINK, 0.12)} !important`,
                      color: `${PINK} !important`,
                    },
                  }}
                >
                  <ToggleButton value="compose">Compose</ToggleButton>
                  <ToggleButton value="preview">
                    <PreviewOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    Preview
                  </ToggleButton>
                </ToggleButtonGroup>

                {previewTab === 'compose' ? (
                  <TextField
                    label={format === 'html' ? 'HTML body' : 'Message'}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    fullWidth
                    multiline
                    minRows={18}
                    maxRows={18}
                    placeholder={
                      format === 'html'
                        ? 'Paste HTML markup here…'
                        : 'Write plain text… use {{userName}} for personalization'
                    }
                    sx={{
                      '& textarea': {
                        overflowY: 'auto !important',
                        resize: 'none',
                        fontFamily:
                          format === 'html'
                            ? 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
                            : 'inherit',
                        fontSize: format === 'html' ? '0.82rem' : '0.95rem',
                        lineHeight: 1.55,
                      },
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      border: `1px solid ${border}`,
                      borderRadius: 2,
                      overflow: 'hidden',
                      height: 420,
                      display: 'flex',
                      flexDirection: 'column',
                      bgcolor: '#f4f8f9',
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderBottom: `1px solid ${border}`,
                        bgcolor: surface,
                        flexShrink: 0,
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 800, color: muted }}>
                        Subject: {subject || '(no subject)'}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ p: 0, flex: 1, minHeight: 0, overflowY: 'auto' }}
                      dangerouslySetInnerHTML={{ __html: previewHtml }}
                    />
                  </Box>
                )}

                <Typography variant="caption" sx={{ color: muted }}>
                  Tokens: {'{{userName}}'}, {'{{name}}'}, {'{{email}}'}, {'{{appUrl}}'} — replaced per recipient.
                </Typography>
              </Box>
            </Box>

            {(activeJobId || jobStatus === 'queued' || jobStatus === 'running') && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  border: `1px solid ${border}`,
                  bgcolor: surface,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                  <CircularProgress size={18} sx={{ color: ACCENT }} />
                  <Typography sx={{ fontWeight: 800 }}>
                    Broadcast {jobStatus || 'queued'} in the background
                  </Typography>
                </Box>
                <LinearProgress sx={{ borderRadius: 1, mb: 1 }} />
                <Typography variant="body2" sx={{ color: muted }}>
                  You can leave this page — emails keep sending on the server.
                  {activeJobId ? ` Job ${activeJobId}.` : ''}
                </Typography>
              </Box>
            )}

            {result && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  border: `1px solid ${border}`,
                  bgcolor: surface,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'center',
                }}
              >
                <CheckCircleIcon sx={{ color: '#1a7f37' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 800 }}>
                    Sent {result.sent} / {result.total}
                    {result.failed ? ` · ${result.failed} failed` : ''}
                  </Typography>
                  {result.errors?.length > 0 && (
                    <Typography variant="caption" sx={{ color: muted, display: 'block', mt: 0.5 }}>
                      {result.errors.slice(0, 3).map((e) => e.email).join(', ')}
                      {result.errors.length > 3 ? '…' : ''}
                    </Typography>
                  )}
                </Box>
                {result.failed > 0 && <ErrorOutlineIcon sx={{ color: '#d97706' }} />}
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.25, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<DeleteOutlineIcon />}
                onClick={clearDraft}
                disabled={sending}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderColor: border,
                  color: muted,
                }}
              >
                Clear draft
              </Button>
              <Button
                variant="contained"
                size="large"
                startIcon={<SendRoundedIcon />}
                onClick={openConfirm}
                disabled={sending || Boolean(activeJobId)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  bgcolor: PINK,
                  px: 2.75,
                  py: 1.2,
                  '&:hover': { bgcolor: '#c40048' },
                }}
              >
                Review & send
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog open={confirmOpen} onClose={() => !sending && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Send this email?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5, color: muted }}>
            You’re about to email <strong>{recipientCount}</strong> recipient
            {recipientCount === 1 ? '' : 's'}
            {audience === 'all' ? ' (all users)' : ' (selected)'}.
          </Typography>
          <Box
            sx={{
              p: 1.75,
              borderRadius: 2,
              border: `1px solid ${border}`,
              bgcolor: isDark ? alpha('#fff', 0.03) : alpha(ACCENT, 0.04),
              mb: 1.5,
            }}
          >
            <Typography variant="caption" sx={{ color: muted, fontWeight: 700, display: 'block' }}>
              SUBJECT
            </Typography>
            <Typography sx={{ fontWeight: 800 }}>{subject}</Typography>
            <Typography variant="caption" sx={{ color: muted, mt: 1, display: 'block' }}>
              Format: {format.toUpperCase()}
              {wrapInLayout ? ' · CrafterHack layout' : ' · Raw body'}
            </Typography>
          </Box>
          {sending && <LinearProgress sx={{ borderRadius: 1, mb: 1 }} />}
          {sending && (
            <Typography variant="body2" sx={{ color: muted }}>
              Queuing broadcast… this only checks SMTP and recipient list.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button disabled={sending} onClick={() => setConfirmOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={sending || Boolean(activeJobId)}
            onClick={send}
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />}
            sx={{ textTransform: 'none', fontWeight: 800, bgcolor: PINK, '&:hover': { bgcolor: '#c40048' } }}
          >
            {sending ? 'Queuing…' : 'Send in background'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminBroadcastEmail;
